import React from "react";
import {
    MdHeadphones, MdAssignment, MdCompare, MdMenuBook, MdSchool,
    MdEditNote, MdSortByAlpha, MdImageSearch, MdCheckCircleOutline,
    MdArrowBack, MdCheckCircle, MdCancel, MdCheck, MdClose,
    MdVolumeUp, MdImage,
} from "react-icons/md";

// ─── Design tokens ───────────────────────────────────────────────────
const TEAL        = "#2a9d8f";
const TEAL_SHADOW = "#258D80";
const TEAL_TINT   = "#2a9d8f1a";
const TEAL_BORDER = "#2a9d8f45";
const GREEN       = "#50C878";
const GREEN_TINT  = "#50C87815";
const RED         = "#e76f51";
const RED_TINT    = "#e76f5115";
const GREY        = "#cccccc";
const FONT        = "'Quicksand', sans-serif";
const LETTERS     = ["A", "B", "C", "D"];

// ─── Badge config ────────────────────────────────────────────────────
const BADGE = {
    "Listening":           { icon: MdHeadphones,         label: "Listening" },
    "Listening to English":{ icon: MdHeadphones,         label: "Listening" },
    "Test":                { icon: MdAssignment,          label: "Test" },
    "Story Review":        { icon: MdMenuBook,            label: "Story Review" },
    "Match":               { icon: MdCompare,             label: "Match" },
    "Match to English":    { icon: MdCompare,             label: "Match" },
    "Story":               { icon: MdMenuBook,            label: "Story" },
    "Writing":             { icon: MdEditNote,            label: "Writing" },
    "Speaking":            { icon: MdVolumeUp,            label: "Speaking" },
    "Basics":              { icon: MdSchool,              label: "Lesson" },
    "Fill In Blank":       { icon: MdEditNote,            label: "Fill In Blank" },
    "Word Order":          { icon: MdSortByAlpha,         label: "Word Order" },
    "Image Choice":        { icon: MdImageSearch,         label: "Image Choice" },
    "True False":          { icon: MdCheckCircleOutline,  label: "True / False" },
};

// ─── Shared small components ──────────────────────────────────────────
function BadgePill({ type }) {
    const cfg = BADGE[type] || { icon: MdSchool, label: type };
    const Icon = cfg.icon;
    return (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: TEAL_TINT, border: `1px solid ${TEAL_BORDER}`, borderRadius: 20, padding: "5px 12px", marginBottom: 14 }}>
            <Icon size={14} color={TEAL} />
            <span style={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: TEAL }}>{cfg.label}</span>
        </div>
    );
}

function ContentCard({ children, style }) {
    return (
        <div style={{ background: "#fff", borderRadius: 18, border: "2px solid #e0e0e0", borderBottom: "5px solid #e0e0e0", padding: 18, marginBottom: 14, marginLeft: 2, marginRight: 2, ...style }}>
            {children}
        </div>
    );
}

function ChoiceRow({ choice, index, answerState, compact }) {
    const letter = LETTERS[index] || String.fromCharCode(65 + index);
    let bg = "#fff", border = "#e0e0e0", textColor = "#000";
    let showCheck = false, showX = false;

    if (answerState === "correct" && choice.correct) {
        bg = GREEN_TINT; border = GREEN; textColor = GREEN; showCheck = true;
    } else if (answerState === "wrong") {
        if (choice._wrongSelected) { bg = RED_TINT; border = RED; textColor = RED; showX = true; }
        else if (choice.correct)   { bg = GREEN_TINT; border = GREEN; textColor = GREEN; showCheck = true; }
    }

    return (
        <div style={{ display: "flex", alignItems: "center", gap: 12, border: `2px solid ${border}`, borderBottom: `4px solid ${border}`, borderRadius: 14, padding: compact ? 12 : 14, marginBottom: 8, background: bg }}>
            {showCheck ? <MdCheckCircle size={22} color={GREEN} /> :
             showX     ? <MdCancel     size={22} color={RED}   /> : (
                <div style={{ width: 28, height: 28, borderRadius: "50%", border: "2px solid #e0e0e0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: "rgba(0,0,0,0.45)" }}>{letter}</span>
                </div>
            )}
            <span style={{ fontFamily: FONT, fontSize: 16, color: textColor, flex: 1, lineHeight: "22px" }}>
                {choice.text || `Choice ${index + 1}`}
            </span>
        </div>
    );
}

function PillChoice({ choice, answerState }) {
    let bg = "#fff", border = "#e0e0e0", color = "#000";
    if (answerState === "correct" && choice.correct)  { bg = GREEN_TINT; border = GREEN; color = GREEN; }
    else if (answerState === "wrong") {
        if (choice._wrongSelected)  { bg = RED_TINT;   border = RED;   color = RED;   }
        else if (choice.correct)    { bg = GREEN_TINT; border = GREEN; color = GREEN; }
    }
    return (
        <div style={{ padding: "10px 18px", border: `2px solid ${border}`, borderRadius: 30, background: bg, color, fontFamily: FONT, fontSize: 15, fontWeight: 600 }}>
            {choice.text || "—"}
        </div>
    );
}

function SentenceWithBlank({ sentence, answerState, correctText }) {
    if (!sentence) return <span style={{ fontFamily: FONT, fontSize: 20, fontWeight: 700, opacity: 0.3 }}>Type your sentence above…</span>;
    const parts = sentence.split("___");
    if (parts.length < 2) return <span style={{ fontFamily: FONT, fontSize: 20, fontWeight: 700, lineHeight: "30px" }}>{sentence}</span>;
    let blankNode;
    if (answerState === "idle") {
        blankNode = <span style={{ color: TEAL, background: TEAL_TINT, border: "1px dashed #2a9d8f80", borderRadius: 6, padding: "0 6px", fontFamily: FONT }}> ___ </span>;
    } else if (answerState === "correct") {
        blankNode = <span style={{ color: GREEN, fontWeight: 700, fontFamily: FONT }}>{correctText || "___"}</span>;
    } else {
        blankNode = <span style={{ color: RED, fontWeight: 700, fontFamily: FONT }}>___</span>;
    }
    return <span style={{ fontFamily: FONT, fontSize: 20, fontWeight: 700, lineHeight: "30px" }}>{parts[0]}{blankNode}{parts[1]}</span>;
}

// ─── Question type renderers ──────────────────────────────────────────
function withWrongFlag(choices, answerState) {
    const firstWrongIdx = choices.findIndex(c => !c.correct);
    return choices.map((c, i) => ({ ...c, _wrongSelected: answerState === "wrong" && !c.correct && i === firstWrongIdx }));
}

function ListeningPreview({ q, answerState }) {
    const choices = withWrongFlag(q.choices || [], answerState);
    return (
        <>
            <BadgePill type={q.type} />
            <ContentCard>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <div style={{ width: 72, height: 72, borderRadius: "50%", border: `2px solid ${TEAL}`, background: TEAL_TINT, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <MdHeadphones size={30} color={TEAL} />
                    </div>
                    <div>
                        <div style={{ fontFamily: FONT, fontSize: 15, fontWeight: 700, lineHeight: "20px", marginBottom: 10 }}>Listen and choose the correct option</div>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: TEAL_TINT, borderRadius: 30, padding: "6px 14px" }}>
                            <div style={{ width: 28, height: 28, borderRadius: "50%", background: TEAL_TINT, border: `2px solid ${TEAL}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <MdVolumeUp size={15} color={TEAL} />
                            </div>
                            <span style={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: `${TEAL}cc` }}>Tap to hear</span>
                        </div>
                    </div>
                </div>
                {q.translation && answerState !== "idle" && (
                    <div style={{ marginTop: 10, fontFamily: FONT, fontSize: 14, opacity: 0.55 }}>{q.translation}</div>
                )}
            </ContentCard>
            {choices.map((c, i) => <ChoiceRow key={i} choice={c} index={i} answerState={answerState} />)}
        </>
    );
}

function TestPreview({ q, answerState }) {
    const choices = withWrongFlag(q.choices || [], answerState);
    return (
        <>
            <BadgePill type={q.type} />
            <ContentCard>
                <div style={{ fontFamily: FONT, fontSize: 18, fontWeight: 700, lineHeight: "27px" }}>
                    {q.question || <span style={{ opacity: 0.3 }}>Question text…</span>}
                </div>
            </ContentCard>
            {choices.map((c, i) => <ChoiceRow key={i} choice={c} index={i} answerState={answerState} />)}
        </>
    );
}

function FillInBlankPreview({ q, answerState }) {
    const correctChoice = (q.choices || []).find(c => c.correct);
    const choices = withWrongFlag(q.choices || [], answerState);
    return (
        <>
            <BadgePill type={q.type} />
            <ContentCard>
                <div style={{ fontFamily: FONT, fontSize: 13, opacity: 0.6, marginBottom: 6 }}>Fill in the missing word</div>
                <SentenceWithBlank sentence={q.sentence} answerState={answerState} correctText={correctChoice?.text} />
                {q.translation && answerState !== "idle" && (
                    <div style={{ marginTop: 10, fontFamily: FONT, fontSize: 14, opacity: 0.55 }}>{q.translation}</div>
                )}
            </ContentCard>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {choices.map((c, i) => <PillChoice key={i} choice={c} answerState={answerState} />)}
            </div>
        </>
    );
}

function WordOrderPreview({ q, answerState }) {
    const words = q.words || [];
    const sentence = (q.sentence || "").trim();
    const prompt = q.prompt || "Put the words in the correct order";
    const sentenceWords = sentence.split(/\s+/).filter(Boolean);

    let zoneBg = "transparent", zoneBorder = "#e0e0e0", zoneDash = "dashed";
    let tileColor = TEAL, tileBg = TEAL_TINT, tileBorder = "#2a9d8f60";
    if (answerState === "correct") { zoneBg = GREEN_TINT; zoneBorder = GREEN; zoneDash = "solid"; tileColor = GREEN; tileBg = GREEN_TINT; tileBorder = GREEN; }
    if (answerState === "wrong")   { zoneBg = RED_TINT;   zoneBorder = RED;   zoneDash = "solid"; tileColor = RED;   tileBg = RED_TINT;   tileBorder = RED;   }

    return (
        <>
            <BadgePill type={q.type} />
            <ContentCard>
                <div style={{ fontFamily: FONT, fontSize: 15, fontWeight: 700, lineHeight: "22px" }}>{prompt}</div>
            </ContentCard>
            <div style={{ minHeight: 56, borderRadius: 14, border: `2px ${zoneDash} ${zoneBorder}`, background: zoneBg, padding: "10px 12px", marginBottom: 12, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
                {answerState === "idle" ? (
                    <span style={{ fontFamily: FONT, fontSize: 13, opacity: 0.35 }}>Tap words below to build your answer</span>
                ) : sentenceWords.map((w, i) => (
                    <div key={i} style={{ padding: "6px 10px", borderRadius: 8, background: tileBg, border: `1.5px solid ${tileBorder}`, fontFamily: FONT, fontSize: 15, fontWeight: 700, color: tileColor }}>{w}</div>
                ))}
            </div>
            {answerState === "wrong" && sentence && (
                <div style={{ background: RED_TINT, borderRadius: 10, padding: "8px 12px", marginBottom: 12, fontFamily: FONT, fontSize: 13, color: RED }}>
                    Correct: <strong>{sentence}</strong>
                </div>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {words.map((w, i) => (
                    <div key={i} style={{ padding: "8px 12px", border: "2px solid #e0e0e0", borderBottom: "4px solid #e0e0e0", borderRadius: 8, background: "#fff", fontFamily: FONT, fontSize: 15, fontWeight: 700 }}>{w}</div>
                ))}
            </div>
        </>
    );
}

function ImageChoicePreview({ q, answerState }) {
    const choices = withWrongFlag(q.choices || [], answerState);
    return (
        <>
            <BadgePill type={q.type} />
            {q.imageUrl ? (
                <img src={q.imageUrl} alt="Choice" style={{ width: "100%", height: 180, objectFit: "cover", borderRadius: 18, border: "2px solid #e0e0e0", marginBottom: 14, display: "block" }} onError={e => { e.target.style.display = "none"; }} />
            ) : (
                <div style={{ width: "100%", height: 180, borderRadius: 18, border: "2px solid #e0e0e0", background: "#f0f0f0", marginBottom: 14, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6 }}>
                    <MdImage size={36} color="#ccc" />
                    <span style={{ fontFamily: FONT, fontSize: 13, color: "#aaa" }}>No image</span>
                </div>
            )}
            {q.question && <div style={{ fontFamily: FONT, fontSize: 16, fontWeight: 700, lineHeight: "22px", margin: "0 2px 12px" }}>{q.question}</div>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {choices.map((c, i) => <ChoiceRow key={i} choice={c} index={i} answerState={answerState} compact />)}
            </div>
            {q.translation && answerState !== "idle" && (
                <div style={{ marginTop: 8, fontFamily: FONT, fontSize: 14, opacity: 0.55 }}>{q.translation}</div>
            )}
        </>
    );
}

function TrueFalsePreview({ q, answerState }) {
    const correctIsTrue = (q.choices || []).find(c => c.text === "True")?.correct;

    const btnStyle = (isTrue) => {
        const isCorrect = isTrue ? correctIsTrue : !correctIsTrue;
        const idleColor = isTrue ? GREEN : RED;
        if (answerState === "idle") return { bg: isTrue ? "#50C87808" : "#e76f5108", border: "#e0e0e0", color: idleColor, opacity: 1 };
        if (answerState === "correct") return isCorrect ? { bg: isTrue ? GREEN_TINT : RED_TINT, border: GREEN, color: GREEN, opacity: 1 } : { bg: "#fff", border: "#e0e0e0", color: GREY, opacity: 0.45 };
        // wrong state: wrong button = red, correct button = 45% opacity
        return isCorrect ? { bg: "#fff", border: "#e0e0e0", color: GREY, opacity: 0.45 } : { bg: RED_TINT, border: RED, color: RED, opacity: 1 };
    };

    const trueS  = btnStyle(true);
    const falseS = btnStyle(false);

    const TFBtn = ({ label, isTrue, style: s }) => (
        <div style={{ flex: 1, borderRadius: 16, border: `2px solid ${s.border}`, borderBottom: `5px solid ${s.border}`, background: s.bg, padding: 16, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, opacity: s.opacity }}>
            {answerState !== "idle" && ((isTrue && answerState === "correct" && correctIsTrue) || (!isTrue && answerState === "correct" && !correctIsTrue))
                ? <MdCheckCircle size={32} color={GREEN} />
                : answerState !== "idle" && !( isTrue ? correctIsTrue : !correctIsTrue ) && answerState !== "idle"
                ? ( answerState === "wrong" && !( isTrue ? correctIsTrue : !correctIsTrue ) ? <MdCancel size={32} color={s.color} /> : (isTrue ? <MdCheck size={32} color={s.color} /> : <MdClose size={32} color={s.color} />) )
                : (isTrue ? <MdCheck size={32} color={s.color} /> : <MdClose size={32} color={s.color} />)
            }
            <span style={{ fontFamily: FONT, fontSize: 18, fontWeight: 700, color: s.color }}>{label}</span>
        </div>
    );

    return (
        <>
            <BadgePill type={q.type} />
            <ContentCard style={{ padding: 22 }}>
                <div style={{ fontFamily: FONT, fontSize: 20, fontWeight: 700, textAlign: "center", lineHeight: "30px" }}>
                    {q.statement || <span style={{ opacity: 0.3 }}>Statement…</span>}
                </div>
            </ContentCard>
            <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                <TFBtn label="True"  isTrue={true}  style={trueS}  />
                <TFBtn label="False" isTrue={false} style={falseS} />
            </div>
            {q.explanation && answerState !== "idle" && (
                <div style={{ background: answerState === "correct" ? GREEN_TINT : RED_TINT, borderRadius: 12, padding: 12, fontFamily: FONT, fontSize: 14, color: answerState === "correct" ? GREEN : RED }}>
                    {q.explanation}
                </div>
            )}
        </>
    );
}

function MatchPreview({ q }) {
    const pairs = q.choices || [];
    const isToEnglish = q.type === "Match to English";
    const pairsReversed = [...pairs].reverse();
    return (
        <>
            <BadgePill type={q.type} />
            <ContentCard>
                <div style={{ fontFamily: FONT, fontSize: 15, fontWeight: 700, lineHeight: "22px" }}>
                    {isToEnglish ? "Match the Amharic word to its English meaning" : "Match the sound with the correct word"}
                </div>
            </ContentCard>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {/* Left column — audio buttons for Match, Amharic text for Match to English */}
                <div>
                    {pairs.map((p, i) => (
                        isToEnglish ? (
                            <div key={i} style={{ border: "2px solid #e0e0e0", borderBottom: "4px solid #e0e0e0", borderRadius: 14, padding: "10px 12px", marginBottom: 8, fontFamily: FONT, fontSize: 14, fontWeight: 700, textAlign: "center", background: "#fff" }}>
                                {p.amharic || `ቃል ${i + 1}`}
                            </div>
                        ) : (
                            <div key={i} style={{ borderRadius: 14, border: `2px solid ${TEAL_BORDER}`, borderBottom: `4px solid ${TEAL_BORDER}`, background: TEAL_TINT, padding: "10px 8px", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
                                <div style={{ width: 30, height: 30, borderRadius: "50%", background: TEAL, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    <MdVolumeUp size={16} color="#fff" />
                                </div>
                                <span style={{ fontFamily: FONT, fontWeight: 700, fontSize: 12, color: TEAL }}>Audio {i + 1}</span>
                            </div>
                        )
                    ))}
                </div>
                {/* Right column — text cards */}
                <div>
                    {pairsReversed.map((p, i) => (
                        <div key={i} style={{ border: "2px solid #e0e0e0", borderBottom: "4px solid #e0e0e0", borderRadius: 14, padding: "10px 12px", marginBottom: 8, fontFamily: FONT, fontSize: 14, fontWeight: 700, textAlign: "center", background: "#fff" }}>
                            {isToEnglish ? (p.english || `English ${i + 1}`) : (p.text || `Word ${i + 1}`)}
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
}

function StoryPreview({ q }) {
    return (
        <>
            <BadgePill type={q.type} />
            {q.imageUrl && <img src={q.imageUrl} alt="Story" style={{ width: "100%", height: 160, objectFit: "cover", borderRadius: 18, border: "2px solid #e0e0e0", marginBottom: 14, display: "block" }} onError={e => { e.target.style.display = "none"; }} />}
            <ContentCard>
                <div style={{ fontFamily: FONT, fontSize: 18, fontWeight: 700, lineHeight: "27px" }}>
                    {q.line || <span style={{ opacity: 0.3 }}>Story line…</span>}
                </div>
                {q.translation && <div style={{ marginTop: 8, fontFamily: FONT, fontSize: 14, opacity: 0.55 }}>{q.translation}</div>}
            </ContentCard>
        </>
    );
}

function WritingPreview({ q }) {
    return (
        <>
            <BadgePill type={q.type} />
            <ContentCard>
                <div style={{ fontFamily: FONT, fontSize: 28, fontWeight: 700, textAlign: "center", padding: "10px 0", color: TEAL }}>
                    {q.writingMaterial || <span style={{ color: "#ccc", fontSize: 18 }}>Writing material…</span>}
                </div>
                {q.translation && <div style={{ textAlign: "center", fontFamily: FONT, fontSize: 14, opacity: 0.55, marginTop: 8 }}>{q.translation}</div>}
            </ContentCard>
            <div style={{ border: "2px dashed #e0e0e0", borderRadius: 14, minHeight: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ fontFamily: FONT, fontSize: 13, opacity: 0.35 }}>Draw or write here</span>
            </div>
        </>
    );
}

function SpeakingPreview({ q }) {
    return (
        <>
            <BadgePill type={q.type} />
            <ContentCard>
                <div style={{ fontFamily: FONT, fontSize: 18, fontWeight: 700, textAlign: "center", lineHeight: "27px" }}>
                    {q.sentence || <span style={{ opacity: 0.3 }}>Sentence…</span>}
                </div>
            </ContentCard>
            <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
                <div style={{ width: 72, height: 72, borderRadius: "50%", background: TEAL_TINT, border: `2px solid ${TEAL}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <MdVolumeUp size={36} color={TEAL} />
                </div>
            </div>
        </>
    );
}

function BasicsPreview({ q, voiceoverAudioUrl, voiceoverSegments, script }) {
    const segments = voiceoverSegments || [];
    const hasVoiceover = !!voiceoverAudioUrl && segments.length > 0;

    // Book-page layout: every part's image + text flows one after another,
    // top to bottom, like pages in a storybook — not a swipeable card.
    if (hasVoiceover) {
        return (
            <>
                <BadgePill type={q.type} />
                {segments.map((seg, i) => (
                    <div key={seg.id} style={{ marginBottom: 26 }}>
                        {seg.imageUrl && (
                            <img
                                src={seg.imageUrl}
                                alt=""
                                style={{ width: "100%", height: 190, objectFit: "cover", borderRadius: 16, marginBottom: 12, display: "block" }}
                                onError={e => { e.target.style.display = "none"; }}
                            />
                        )}
                        {seg.emphasisText && (
                            <div style={{ fontFamily: FONT, fontSize: 26, fontWeight: 700, lineHeight: "32px", color: "#000", margin: "0 0 8px" }}>
                                {seg.emphasisText}
                            </div>
                        )}
                        <p style={{ fontFamily: FONT, fontSize: 16, fontWeight: 600, lineHeight: "26px", color: "#222", margin: 0 }}>
                            {seg.text || <span style={{ opacity: 0.35, fontWeight: 400 }}>Part {i + 1} — no script assigned</span>}
                        </p>
                    </div>
                ))}
            </>
        );
    }

    // No voiceover parts yet: show the lesson script as plain book text.
    const paragraphs = (script || "").split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
    return (
        <>
            <BadgePill type={q.type} />
            {paragraphs.length > 0 ? (
                paragraphs.map((para, i) => (
                    <p key={i} style={{ fontFamily: FONT, fontSize: 16, fontWeight: 600, lineHeight: "26px", color: "#222", margin: "0 0 18px", whiteSpace: "pre-wrap" }}>
                        {para}
                    </p>
                ))
            ) : (
                <div style={{ fontFamily: FONT, fontSize: 13, color: "#aaa", textAlign: "center", marginTop: 40 }}>
                    No script yet — write one in the Script card.
                </div>
            )}
        </>
    );
}

function renderContent(q, answerState, voiceoverAudioUrl, voiceoverSegments, script) {
    if (!q) return <div style={{ fontFamily: FONT, fontSize: 13, color: "#aaa", textAlign: "center", marginTop: 40 }}>No question selected</div>;
    switch (q.type) {
        case "Listening":
        case "Listening to English":  return <ListeningPreview  q={q} answerState={answerState} />;
        case "Test":                  return <TestPreview        q={q} answerState={answerState} />;
        case "Story Review":          return <TestPreview        q={q} answerState={answerState} />;
        case "Match":
        case "Match to English":      return <MatchPreview       q={q} />;
        case "Fill In Blank":         return <FillInBlankPreview q={q} answerState={answerState} />;
        case "Word Order":            return <WordOrderPreview   q={q} answerState={answerState} />;
        case "Image Choice":          return <ImageChoicePreview q={q} answerState={answerState} />;
        case "True False":            return <TrueFalsePreview   q={q} answerState={answerState} />;
        case "Story":                 return <StoryPreview       q={q} />;
        case "Writing":               return <WritingPreview     q={q} />;
        case "Speaking":              return <SpeakingPreview    q={q} />;
        case "Basics":                return <BasicsPreview      q={q} voiceoverAudioUrl={voiceoverAudioUrl} voiceoverSegments={voiceoverSegments} script={script} />;
        default:                      return null;
    }
}

// ─── Main export ──────────────────────────────────────────────────────
function PhonePreview({ question, answerState, scale: scaleProp, voiceoverAudioUrl, voiceoverSegments, script }) {
    const SCALE = scaleProp ?? (340 / 390);
    const CLIP_H = Math.round(750 * SCALE);

    const isWO = question?.type === "Word Order";
    const continueActive = answerState !== "idle";
    const btnLabel = isWO && !continueActive ? "CHECK" : "CONTINUE";
    const btnBg = continueActive ? TEAL : (isWO ? TEAL : GREY);
    const btnBorder = continueActive ? TEAL_SHADOW : GREY;

    return (
        <div style={{ width: Math.round(390 * SCALE), height: CLIP_H, overflow: "hidden", borderRadius: Math.round(50 * SCALE), boxShadow: "0 8px 32px rgba(0,0,0,0.22)", margin: "0 auto" }}>
            <div style={{ width: 390, height: 844, transform: `scale(${SCALE})`, transformOrigin: "top left", background: "#f5f5f5", borderRadius: 50, border: "2px solid #1a1a1a", position: "relative", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                {/* Notch */}
                <div style={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", width: 120, height: 30, background: "#111", borderRadius: "0 0 18px 18px", zIndex: 20 }} />
                {/* Top bar */}
                <div style={{ paddingTop: 48, paddingLeft: 16, paddingRight: 16, paddingBottom: 10, display: "flex", alignItems: "center", gap: 12, background: "#f5f5f5", flexShrink: 0 }}>
                    <MdArrowBack size={28} color="#000" style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1, height: 30, borderRadius: 20, border: "2px solid #e0e0e0", background: "#fff", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: "60%", background: TEAL, borderRadius: 20 }} />
                    </div>
                </div>
                {/* Content area */}
                <div style={{ flex: 1, overflowY: "auto", padding: "16px 16px 0" }}>
                    {renderContent(question, answerState, voiceoverAudioUrl, voiceoverSegments, script)}
                </div>
                {/* Bottom button */}
                <div style={{ padding: "8px 16px 36px", background: "#f5f5f5", flexShrink: 0 }}>
                    <button type="button" style={{ width: "100%", padding: "10px 0", borderRadius: 15, border: `2px solid ${btnBorder}`, borderBottom: `3px solid ${btnBorder}`, background: btnBg, color: "#fff", fontFamily: FONT, fontSize: 20, fontWeight: 700, cursor: "default" }}>
                        {btnLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default PhonePreview;
